<template>
    <div>
        <h3>ノートを記述する</h3>
        <div>
            <p>
                本文編集画面には、ノートの機能があります。
            </p>
            <p>
                ノートは、主に小説の梗概、登場人物、設定、用語集などの資料をまとめたもので、
                最大で５つのタブを追加できます。
                ノートに小説の設計図を記述し、それを元に本文を書いていくという一般的な利用法を想定していますが、
                それ意外にも簡単な覚え書きを記録したり、年表や詳細設定を記述することができます。
                「こうしないと使えない」という用法を限定するものではありません。
                また、このノートは小説や本文を追加しなくても、独立して編集することも可能です。
            </p>
            <img src="/img/help/novel/11.png" alt="ノート" class="img-thumbnail">

            <h4>ノート編集</h4>
            <img src="/img/help/novel/12.png" alt="ノート編集ボタン" class="img-thumbnail mb-3">
            <p>
                本文を追加せずにノートだけを編集したい場合、
                本文一覧画面から「ノート編集」ボタンをクリックして、ノート編集画面を開くことができます。<br>
                ノートの編集画面は、初期状態では一括置換、小説概要、新規ノートの３つのタブがあります。
            </p>
            <h5>新規ノート</h5>
            <p>
                新規ノートタブを開いて、ノートの見出しを入力したら、追加ボタンでノートを追加できます。
                例えば「梗概」と記述して追加してみてください。
                タブに「梗概」と表示された空のノートが追加されます。
                このノートにはこれから書こうとしている小説のあらすじを記述することができます。
            </p>
            <h5>ノートの限界数</h5>
            <p>
                小説に属すノートは {{$store.state.max_notes}} ページまでしか追加することができません。
                それ以上のノートを追加しても、ノートの管理が煩雑になり、
                肝心の小説の執筆の助けにならない可能性があるためです。<br>
                ノートを複数追加した状態では、スマートフォンの場合はタブがすべて表示できないことがあります。
                そのときは、タブ部分をフリックすることで、横にスクロールすることができます。<br>
                小説に属さないノートは120件まで追加することができます（場合によっては120件を超えることがあります）。
            </p>
            <h5>ノートの保存</h5>
            <p>
                ノートに記載した内容は自動保存されます。
                任意に保存する必要がありませんが、全て選択して削除すると、空のノートが上書き保存されてしまいます。
                ご注意ください。
            </p>
            <p>
                ノートを保存できる最大サイズは {{$filters.numberFormat($store.state.max_note_size )}} バイトとなっていますが、
                内部的にはバイト数ではなく文字数でカウントしています。
                実際のデータサイズはこれよりも大きくなることがあります。
                この最大サイズを超えるノートを記述すると保存できなくなります。
                長大な梗概を記述する場合はノートを分解してください。
            </p>
            <h5>ノートの順番変更</h5>
            <p>
                ノート画面の左下に＜あるいは＞のアイコンのボタンがあります。
                このボタンでノートの順番を変更することができます。
            </p>
            <h5>ノートを外す</h5>
            <p>
                小説に設定されたノートは「外す」という処理が実行できます。
                ノートを小説から外すと、小説に属さない独立したノートとして、ノート一覧にリストされます。
                <strong>ノートを削除する場合は、一度小説から外してノート一覧から削除を行ってください。</strong>
            </p>
            <h5>ノートの削除</h5>
            <p>
                小説に属さない独立したノートのみ削除することができます。
                ノート画面の右下に「ノートを削除」ボタンがあります。
                ここから現在のノートを削除することができます。
                削除されたノートはもとに戻せないのでご注意ください。
            </p>
            <h5>検索と一括置換</h5>
            <img src="/img/help/novel/14.png" alt="横断検索と一括置換" class="img-thumbnail">
            <p>
                虫眼鏡アイコン<i class="fas fa-search"></i>のタブで、ノートの全体検索と一括置換ができます。
                ここから検索を開いて検索文字列を入力して検索を実行すると、その結果が一覧表示されます。
                一覧をクリックすると該当箇所へジャンプします。
            </p>
            <p>
                また置換文字列を入力して置換を実行すると、すべての検索結果を指定の文字列で置き換えます。
                置換文字列を空欄にしたまま置換を実行すると、すべての検索結果を削除します。
                もとに戻せないのでご注意ください。
            </p>
            <p>
                この検索ウインドウを閉じても、検索結果は消えません。
            </p>
            <h5>小説概要</h5>
            <p>
                小説のサブタイトルと紹介文が表示されます。
                ここからそれらの情報を編集することはできません。
            </p>

            <h4>エディタのノートパネル</h4>
            <img src="/img/help/novel/13.png" alt="ノートパネル" class="img-thumbnail">
            <p>
                小説のエディタ画面右上にスティッキーノートアイコンがあります。
                これをクリックすると、ノートサイドパネルが開きます。
            </p>
            <p>
                このサイドパネルには小説概要は表示されませんが、ノート編集画面と同一の機能を持ちます。
                小説を執筆しながら、ノートを適宜追加、編集、削除することができます。
            </p>

            <h4>ブロックエディタ</h4>
            <p>
                ノートはブロックエディタになっています。
                文章を記述すると、ブロック単位でフォーマットを指定することができます。
            </p>
            <img src="/img/help/novel/32.png" alt="ブロックエディタ" class="img-thumbnail mb-3">
            <p>
                記述した文章の一部あるいは全部を選択すると、インラインツールバーが表示されます。
                ドロップダウンからブロックタイプを、見出し、リスト、チェックリストなどに変換することができます。
                あるいは、選択した文字列を、<b>太字</b>、<i>斜体</i>にしたり、<u>下線</u>を引くこともできます。
            </p>
            <img src="/img/help/novel/33.png" alt="フォーマットされたエディタ画面" class="img-thumbnail mb-3">
            <h5>特殊な操作</h5>
            <p>
                Ctrl+A(⌘+A)による全文選択はブロック単位となります。
                Ctrl+A(⌘+A)の操作を２回続けて実行すると、全ブロックを選択します。
                この状態では、削除とコピーはできますが、貼り付けはできません。
                別の文章と置換する場合は一度削除してから貼り付けてください。
            </p>
            <p>
                複数行のテキストを貼り付けてリスト化することはできません。
                Googleドキュメントやワープロアプリなどでリスト化したデータをコピーして貼り付けるとリストとして整形します。
            </p>
            <p>
                Enterキーで改行すると、新規ブロックが追加されます。
                同一のブロック内で改行する場合は、Shift+Enterしてください。
            </p>
            <h5>画像の貼り付け</h5>
            <p>
                ノートのブロックエディタはいくつかの画像形式に対応しています。
                必要に応じて、ノートを補完するための画像を貼り付けることができます。
            </p>
            <h6>画像URL</h6>
            <p class="pl-3">
                末尾が画像ファイルの拡張子で終わるURLを貼り付けると、その画像を読み込んで表示します。
                画像ファイルの拡張子でないURLを貼り付けても、画像URLと認識しません。
                対応する拡張子は、gif, jpg(jpeg), tiff, png, webpです。
            </p>
            <h6>画像ファイルのドラッグアンドドロップ</h6>
            <p class="pl-3">
                画像ファイルをドラッグアンドドロップで貼り付けることもできます。
                この方法では、<b>画像データがドキュメントに埋め込まれるため、ノートの最大サイズを超過しないものを選ばなければなりません。</b>
            </p>
            <h6>Gyazo.comのキャプチャ</h6>
            <p class="pl-3">
                <a href="https://gyazo.com" target="_blank">
                    Gyazo.com
                    <i class="fas fa-external-link-alt"></i>
                </a>のキャプチャ画像のURLを貼り付けることができます。
                キャプチャ画像のプレビュー画面のURLをそのまま貼り付けてください。
            </p>
            <img src="/img/help/novel/38.png" alt="Gyazo.com" class="img-thumbnail mb-3">

            <h4>アウトライン</h4>
            <img src="/img/help/novel/19.png" alt="アウトライン" class="img-thumbnail mb-3">
            <p>
                ノートタブ右上の
                <i class="fas fa-chevron-circle-down"></i>
                アイコンから、ノートのアウトラインを表示することができます。
                アウトラインによって、文字数の多いノートの見出しを素早く探し出すことができます。
            </p>
            <p>
                <b>見出し</b>を記述することで自動的にアウトラインの一覧を作成します。
            </p>
            <h4>ノートのエクスポート</h4>
            <p>
                小説のバックアップデータに、ノートの内容がMarkdown記法で出力されます。
                このMarkdownは完璧ではなく、一部のフォーマットが再現できないことがあります。
            </p>
        </div>
    </div>
</template>

<style src="../help.scss" scoped lang="scss"></style>

<script>
export default {
    created() {
        this.useHead({
            title: "ノートを記述する | 貸し本棚ヘルプ",
            link: [
                {
                    rel: "canonical",
                    href: location.origin + location.pathname,
                    id: "canonical"
                }
            ]
        });
    }
}
</script>